import Footer from "components/sections/footer"
import Header from "components/sections/headerV2"
import Seo from "components/sections/seo"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import { getCorporationSchema, getSchemaWithFallback } from "../../utils/schemaHelpers"
import Template from "./template"

const TemplateContainer = ({ data: { footer, header, template } }) => {
  const fallbackSchema = getCorporationSchema()
  
  // Use Contentful json if present, otherwise use fallback
  const schema = getSchemaWithFallback(template.meta?.json, fallbackSchema)

  return (
    <>
      <Seo
        {...template.meta}
        id={template.id}
        schemaMarkup={schema}
        slug={template.slug}
      />
      <Header {...header} />
      <Template {...template} />
      <Footer {...footer} />
    </>
  )
}

TemplateContainer.props = {
  ...Template.props,
  meta: PropTypes.shape(Seo.props),
}

TemplateContainer.propTypes = {
  data: PropTypes.shape({
    footer: PropTypes.shape(Footer.props),
    header: PropTypes.shape(Header.props),
    template: PropTypes.shape(TemplateContainer.props),
  }),
}

export default TemplateContainer

export const query = graphql`
  fragment ButtonV2Fields on ContentfulComponentButtonV2 {
    id
    accessibilityLabel
    showArrow
    buttonSize
    buttonStyle
    path
    text
  }
  fragment ImageFields on ContentfulAsset {
    title
    description
    file {
      url
    }
  }
  query CareersPage($slug: String) {
    header: contentfulGlobalHeader(name: { eq: "Header" }) {
      announcement {
        json
      }
      menu {
        menus {
          label
          links {
            text
            path
            nestedLinks {
              text
              path
            }
          }
        }
      }
      utilityLinks {
        path
        text
      }
      cta {
        href: path
        text
      }
      callsToAction {
        ...ButtonV2Fields
      }
    }
    footer: contentfulGlobalFooter(name: { eq: "Footer" }) {
      menu {
        menus {
          label
          links {
            path
            text
          }
        }
      }
      scheduleAppointment {
        path
        text
      }
      socialLinks {
        path
        text
      }
      utilityLinks {
        path
        text
      }
    }
    template: contentfulCareersPage(slug: { eq: $slug }) {
      id
      slug
      title
      gallery {
        title
        file {
          url
        }
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      meta: seoMetadata {
        canonical
        keywords
        title
        type
        json
        abstract {
          abstract
        }
        description {
          description
        }
        image {
          file {
            url
          }
        }
      }
      sections {
        __typename
        ... on ContentfulSectionTeamTestimonial {
          contentTitle {
            ... on ContentfulSectionWrapper {
              __typename
              backgroundStyle

              wrapperSections: sections {
                __typename

                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
          }
          teamTestimonials {
            title
            subTitle
            quote {
              quote
            }
            rating
          }
        }
        ... on ContentfulSectionReferralActions {
          __typename
          sectionTitle
          contentTitle {
            ... on ContentfulSectionWrapper {
              __typename
              backgroundStyle

              wrapperSections: sections {
                __typename

                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
          }
          referralButtons {
            accessibilityLabel
            color
            isOutlined
            isSmall
            href: path
            text
            tooltipText {
              json
            }
          }
        }

        ... on ContentfulSectionSplitContent {
          __typename
          backgroundColor
          reverse
          image {
            image {
              alt: description
              title
              file {
                url
              }
            }
          }
          intro {
            eyebrow: subTitle
            action: callToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            secondaryAction: secondaryCallToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
        }
        ... on ContentfulSectionExploreRoles {
          __typename
          contentTitle {
            ... on ContentfulSectionWrapper {
              __typename
              backgroundStyle

              wrapperSections: sections {
                __typename

                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
          }
          roles {
            roleTitle
            content {
              json
            }
          }
        }

        ... on ContentfulTemplateSection {
          __typename
          title
          additionalSettings {
            enableDirectionsCTA
          }
        }
        ... on ContentfulSectionInfoCardGrid {
          contentTitle {
            ... on ContentfulSectionWrapper {
              __typename
              backgroundStyle

              wrapperSections: sections {
                __typename

                ... on ContentfulSectionRichText {
                  __typename
                  alignment
                  details {
                    json
                  }
                }
              }
            }
          }
          cards {
            backgroundColor

            media {
              image {
                alt: description
                title
                file {
                  url
                }
              }
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
          layoutStyle
        }

        ... on ContentfulSectionDynamic {
          __typename
          dynamicSection: sections
          backgroundColor
          style
          title
          additionalSettings {
            enableDirectionsCTA
            testimonialsCount
          }
          additionalText {
            json
          }
        }
      }
    }
  }
`
